/**
 * What is in force for this template, and the two template-wide actions (D10).
 *
 * THE SOURCE IN WORDS, NOT ONLY IN COLOUR. "Systemvorlage" or "Eigene
 * Vorlage", with the server's version number beside an own template only — the
 * system template has no version, and none is invented for it. A draft that
 * is not saved says so for as long as it is not: a new one as "Neue eigene
 * Vorlage, noch nicht gespeichert", an edit of the stored one as
 * "Ungespeicherte Änderungen" — one sentence each, never both.
 *
 * TWO ACTIONS, EACH ONLY WHERE IT MEANS SOMETHING. "Eigene Vorlage erstellen"
 * where the server allows authoring and the system template is in force;
 * "Auf Systemvorlage zurücksetzen" where an own template is in force. A refused
 * revert shows the server's sentence here, and on a conflict the way to load
 * the current template.
 *
 * AND A THIRD SLOT, FILLED BY THE CALLER (TEST-EMAIL T3): the test send, beside
 * the two template-wide actions because it sends exactly the template this card
 * says is in force. This card does not decide whether it exists or may open —
 * it places what it is given: the action in the row of buttons, and the
 * sentence that explains a blocked one as a row of its own.
 */
import React from "react";

import { templateSourceLabel } from "./automationTemplateLabels";

const PRIMARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-primary bg-primary px-3.5 py-1.5 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const SECONDARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-border bg-card px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const BADGE =
  "inline-flex items-center rounded-full border px-2 py-0.5 text-[11.5px] font-semibold";

export default function AutomationTemplateStatus({
  source,
  latestVersion,
  creating,
  dirty,
  canAuthor,
  canCreate,
  onCreate,
  canRevert,
  revertDisabled,
  onRevert,
  revertError,
  onReload,
  testEmailAction = null,
  testEmailNote = null,
}) {
  const isCustom = source === "custom";
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-[14px] border border-border bg-card p-3.5">
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`${BADGE} ${
              isCustom
                ? "border-primary/30 bg-primary/10 text-primary"
                : "border-border bg-muted/50 text-foreground"
            }`}
          >
            {templateSourceLabel(source)}
          </span>
          {isCustom && latestVersion !== null && latestVersion !== undefined ? (
            <span className={`${BADGE} border-border text-muted-foreground`}>
              Version {latestVersion}
            </span>
          ) : null}
          {creating ? (
            <span className={`${BADGE} border-border text-foreground`}>
              Neue eigene Vorlage, noch nicht gespeichert
            </span>
          ) : null}
          {dirty && !creating ? (
            <span className={`${BADGE} border-border text-foreground`}>
              Ungespeicherte Änderungen
            </span>
          ) : null}
        </div>
        <p className="m-0 text-[12px] text-muted-foreground">
          {isCustom
            ? "Für neue E-Mails dieser Automatisierung gilt die eigene Vorlage."
            : "Diese Vorlage verwendet derzeit die Systemvorlage."}
        </p>
        {!canAuthor ? (
          <p className="m-0 text-[12px] text-muted-foreground">
            Für diese Automatisierung kann keine eigene Vorlage erstellt werden.
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {canCreate ? (
          <button type="button" onClick={onCreate} className={PRIMARY_BUTTON}>
            Eigene Vorlage erstellen
          </button>
        ) : null}
        {canRevert ? (
          <button
            type="button"
            onClick={onRevert}
            disabled={revertDisabled}
            className={SECONDARY_BUTTON}
          >
            Auf Systemvorlage zurücksetzen
          </button>
        ) : null}
        {testEmailAction}
      </div>
      {testEmailNote ? (
        <p id={testEmailNote.id} className="m-0 w-full text-[12px] text-muted-foreground">
          {testEmailNote.text}
        </p>
      ) : null}
      {revertError ? (
        <div className="flex w-full flex-col items-start gap-2">
          <p role="alert" className="m-0 text-[12.5px] text-destructive">
            {revertError.message}
          </p>
          {revertError.code === "template_version_conflict" ? (
            <button type="button" onClick={onReload} className={SECONDARY_BUTTON}>
              Aktuelle Vorlage laden
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
